// UnitsMobile.jsx — onglet Unités (mobile). CRUD complet.
// Mêmes endpoints que GestionUnites.js (/api/units).
import React, { useMemo, useState } from "react";
import { message, Modal } from "antd";
import axios from "axios";
import API_CONFIG from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import {
  ProductScreen, Appbar, Summary, SearchBar, Chips, EmptyState, Fab, Sheet, ActionRow,
  Field, TextInput, SelectInput, Segmented, Mono, Pill, Ico,
  useResourceList, useMobileMenu, genSlug,
} from "./productMobileKit";

const API_URL = API_CONFIG.BASE_URL;
const PERM = {
  create: "Gestion Commerciale.Produits.Unites.create",
  edit: "Gestion Commerciale.Produits.Unites.edit",
  delete: "Gestion Commerciale.Produits.Unites.delete",
};
const OPERATORS = [{ value: "*", label: "×" }, { value: "/", label: "÷" }, { value: "+", label: "+" }, { value: "-", label: "−" }];
const opLabel = (u) => (u.operator ? `${u.operator} ${u.operator_value ?? ""}`.trim() : null);

export default function UnitsMobile({ onMenu: onMenuProp }) {
  const outletMenu = useMobileMenu();
  const onMenu = onMenuProp || outletMenu;
  const { hasPermission } = useAuth();
  const { selectedCompany } = useSelection();
  const can = { create: hasPermission(PERM.create), edit: hasPermission(PERM.edit), delete: hasPermission(PERM.delete) };

  const { rows: units, loading, reload } = useResourceList(
    async () => {
      if (!selectedCompany) return [];
      const res = await axios.get(`${API_URL}/api/units`, { params: { company_id: selectedCompany, limit: 1000 } });
      return res.data?.units || [];
    },
    [selectedCompany]
  );

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [sheet, setSheet] = useState(null);
  const [form, setForm] = useState({ name: "", short_name: "", base_unit: "", operator: "*", operator_value: "1", parent_id: null });
  const [saving, setSaving] = useState(false);

  const roots = useMemo(() => units.filter((u) => !u.parent_id).length, [units]);
  const derived = useMemo(() => units.filter((u) => u.parent_id).length, [units]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return units.filter((u) => {
      if (filter === "roots" && u.parent_id) return false;
      if (filter === "derived" && !u.parent_id) return false;
      if (q && ![u.name, u.short_name, u.base_unit].some((v) => String(v || "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [units, query, filter]);

  const parentOptions = useMemo(() => {
    const selfId = sheet?.unit?.id;
    return units.filter((u) => u.id !== selfId).map((u) => ({ value: u.id, label: `${u.name} (${u.short_name})` }));
  }, [units, sheet]);

  const openCreate = () => {
    if (!selectedCompany) return message.warning("Veuillez sélectionner une entreprise avant de créer une unité");
    setForm({ name: "", short_name: "", base_unit: "", operator: "*", operator_value: "1", parent_id: null });
    setSheet({ mode: "form", unit: null });
  };
  const openEdit = (u) => {
    setForm({
      name: u.name || "", short_name: u.short_name || "", base_unit: u.base_unit || "",
      operator: u.operator || "*", operator_value: u.operator_value != null ? String(u.operator_value) : "1",
      parent_id: u.parent_id || null,
    });
    setSheet({ mode: "form", unit: u });
  };
  const openActions = (u) => {
    if (can.edit && !can.delete) return openEdit(u);
    if (!can.edit && !can.delete) return;
    setSheet({ mode: "actions", unit: u });
  };
  const close = () => { if (!saving) setSheet(null); };

  const submit = async () => {
    if (!form.name.trim()) return message.warning("Le nom est requis");
    if (!form.short_name.trim()) return message.warning("Le nom court est requis");
    if (!/^\d+$/.test(String(form.operator_value))) return message.warning("La valeur de l'opérateur doit être un nombre");
    setSaving(true);
    try {
      const payload = {
        company_id: selectedCompany,
        name: form.name.trim(), short_name: form.short_name.trim(),
        slug: genSlug(form.name), base_unit: form.base_unit || "",
        operator: form.operator, operator_value: Number(form.operator_value),
        parent_id: form.parent_id || null,
      };
      if (sheet.unit) { await axios.put(`${API_URL}/api/units/${sheet.unit.id}`, payload); message.success("Unité mise à jour avec succès"); }
      else { await axios.post(`${API_URL}/api/units`, payload); message.success("Unité créée avec succès"); }
      setSheet(null); reload();
    } catch (err) { message.error(err.response?.data?.error || "Erreur lors de l'enregistrement de l'unité"); }
    finally { setSaving(false); }
  };

  const askDelete = (u) => {
    setSheet(null);
    Modal.confirm({
      title: "Supprimer cette unité ?", content: "Cette action pourrait affecter les produits ou unités associés.",
      okText: "Oui, Supprimer", okType: "danger", cancelText: "Non", centered: true,
      onOk: async () => {
        try { await axios.delete(`${API_URL}/api/units/${u.id}`); message.success("Unité supprimée avec succès"); reload(); }
        catch (err) {
          if (err.response?.status === 409 && err.response?.data?.error) message.error(err.response.data.error);
          else message.error("Erreur lors de la suppression de l'unité.");
        }
      },
    });
  };

  return (
    <ProductScreen>
      <Appbar title="Unités" onMenu={onMenu}
        subtitle={`${units.length} unité${units.length > 1 ? "s" : ""} de mesure`}
        action={<button className="iconbtn"><Ico name="ruler" /></button>} />
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0, gap: 12 }}>
        <Summary items={[
          { label: "Unités", value: units.length },
          { label: "De base", value: roots },
          { label: "Dérivées", value: derived },
        ]} />
        <SearchBar value={query} onChange={setQuery} placeholder="Rechercher une unité…" />
        <Chips value={filter} onChange={setFilter} options={[
          { key: "all", label: "Toutes" }, { key: "roots", label: "De base" }, { key: "derived", label: "Dérivées" },
        ]} />
      </div>

      <div className="body">
        {loading || !visible.length ? (
          <EmptyState icon="ruler" loading={loading}
            title={loading ? "Chargement des unités" : (query || filter !== "all" ? "Aucun résultat" : "Aucune unité")}
            text={loading ? "Synchronisation en cours." : "Définissez vos unités de mesure (kg, pièce, litre…)."} />
        ) : (
          <div className="list">
            {visible.map((u) => (
              <button key={u.id} className="rcard" onClick={() => openActions(u)}>
                <Mono name={u.short_name || u.name} square />
                <div className="rcard__main">
                  <div className="rcard__name">{u.name}</div>
                  <div className="rcard__meta">
                    <span className="rcard__sub">{u.short_name}{u.base_unit ? ` · base ${u.base_unit}` : ""}</span>
                    {opLabel(u) ? <Pill tone="info" label={opLabel(u)} /> : null}
                  </div>
                </div>
                <span className="rcard__chev"><Ico name="chevron-right" /></span>
              </button>
            ))}
          </div>
        )}
      </div>

      {can.create ? <Fab onClick={openCreate} label="Nouvelle" /> : null}

      <Sheet open={sheet?.mode === "form"} onClose={close}
        title={sheet?.unit ? "Modifier l'unité" : "Nouvelle unité"}
        subtitle="Nom, abréviation et règle de conversion.">
        <div className="field-row">
          <Field label="Nom"><TextInput value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} placeholder="Ex : Kilogramme" autoFocus /></Field>
          <Field label="Nom court"><TextInput value={form.short_name} onChange={(v) => setForm((f) => ({ ...f, short_name: v }))} placeholder="kg" /></Field>
        </div>
        <Field label="Unité de base" hint="facultatif">
          <TextInput value={form.base_unit} onChange={(v) => setForm((f) => ({ ...f, base_unit: v }))} placeholder="Ex : g" />
        </Field>
        <Field label="Opérateur de conversion">
          <Segmented value={form.operator} onChange={(v) => setForm((f) => ({ ...f, operator: v }))} options={OPERATORS} />
        </Field>
        <Field label="Valeur de conversion">
          <TextInput value={form.operator_value} onChange={(v) => setForm((f) => ({ ...f, operator_value: v.replace(/[^\d]/g, "") }))} placeholder="Ex : 1000" inputMode="numeric" icon="sliders-horizontal" />
        </Field>
        <Field label="Unité parente" hint="facultatif">
          <SelectInput value={form.parent_id} onChange={(v) => setForm((f) => ({ ...f, parent_id: v }))} options={parentOptions} placeholder="Aucune" />
        </Field>
        <div className="sheet__actions">
          <button className="btn btn--ghost" onClick={close} disabled={saving}>Annuler</button>
          <button className="btn btn--primary" onClick={submit} disabled={saving || !form.name.trim() || !form.short_name.trim()}>
            <Ico name={saving ? "loader-circle" : "check"} className={saving ? "spin" : ""} />
            {sheet?.unit ? "Mettre à jour" : "Créer l'unité"}
          </button>
        </div>
      </Sheet>

      <Sheet open={sheet?.mode === "actions"} onClose={close}>
        {sheet?.unit ? (
          <>
            <div className="preview">
              <Mono name={sheet.unit.short_name || sheet.unit.name} square />
              <div>
                <div className="preview__name">{sheet.unit.name}</div>
                <div className="preview__slug">{sheet.unit.short_name}{opLabel(sheet.unit) ? ` · ${opLabel(sheet.unit)}` : ""}</div>
              </div>
            </div>
            {can.edit ? <ActionRow icon="pencil" label="Modifier l'unité" onClick={() => openEdit(sheet.unit)} /> : null}
            {can.delete ? <ActionRow icon="trash-2" label="Supprimer l'unité" danger onClick={() => askDelete(sheet.unit)} /> : null}
          </>
        ) : null}
      </Sheet>
    </ProductScreen>
  );
}
